import { useRef, useState } from 'react';
import { AlertCircle, ExternalLink, FileUp, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import {
  PO_IMPORT_ACCEPT,
  isImportableOrderFile,
  type ExtractConfidence,
  type Prefill,
} from '@/features/procurement/poImport';
import type { ReviewIssue, ReviewTone } from './poImportTone';

const CONFIDENCE_LABEL: Record<ExtractConfidence, string> = {
  high: 'Read clearly',
  medium: 'Read with some interpretation',
  low: 'Low confidence — double-check',
};

interface ReviewTagProps {
  tone: ReviewTone | null | undefined;
  prefill?: Prefill;
  hint?: string;
  className?: string;
}

/** Small tag beside a field label: "From PDF", "Check" or "Needs input". */
export function ReviewTag({ tone, prefill, hint, className }: ReviewTagProps) {
  if (!tone) return null;
  const label = tone === 'required' ? 'Needs input' : tone === 'check' ? 'Check' : 'From PDF';
  const tag = (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-medium uppercase leading-4 tracking-wide',
        tone === 'required' && 'border-transparent bg-fresh-salmon-dark text-linen',
        tone === 'check' && 'border-fresh-salmon/60 bg-fresh-salmon/25 text-fresh-salmon-dark',
        tone === 'prefill' && 'border-info-soft/60 bg-info-soft/30 text-info',
        className,
      )}
    >
      {tone === 'prefill' ? (
        <Sparkles className="h-2.5 w-2.5" />
      ) : (
        <AlertCircle className="h-2.5 w-2.5" />
      )}
      {label}
    </span>
  );
  const details = [
    hint,
    prefill ? CONFIDENCE_LABEL[prefill.confidence] : null,
    prefill?.evidence ? `On the document: “${prefill.evidence}”` : null,
  ].filter(Boolean);
  if (details.length === 0) return tag;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="inline-flex cursor-help">
          {tag}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-1">
        {details.map((d) => (
          <p key={d}>{d}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}

/** Inline explanation under a field that still needs a look. */
export function ReviewHint({ tone, children }: { tone: ReviewTone | null; children?: string }) {
  if (!children || (tone !== 'required' && tone !== 'check')) return null;
  return (
    <p
      className={cn(
        'text-xs',
        tone === 'required' ? 'text-fresh-salmon-dark' : 'text-muted-foreground',
      )}
    >
      {children}
    </p>
  );
}

interface ImportDropzoneProps {
  busy: boolean;
  onFile: (file: File) => void;
  onReject: (message: string) => void;
}

/** "Ordered online? Drop the order PDF" — the entry point on a new PO. */
export function PoImportDropzone({ busy, onFile, onReject }: ImportDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const take = (files: FileList | null | undefined) => {
    const file = files?.[0];
    if (!file) return;
    if (!isImportableOrderFile(file)) {
      onReject('Drop the order as a PDF (or a PNG / JPG screenshot).');
      return;
    }
    onFile(file);
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!busy) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (!busy) take(e.dataTransfer.files);
      }}
      className={cn(
        'flex flex-col gap-3 rounded-md border border-dashed px-4 py-3 transition-colors sm:flex-row sm:items-center',
        dragging
          ? 'border-atmosphere-dark bg-atmosphere/25'
          : 'border-atmosphere-dark/40 bg-atmosphere/10 dark:border-atmosphere/40',
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {busy ? (
          <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-info" />
        ) : (
          <FileUp className="mt-0.5 h-5 w-5 shrink-0 text-info" />
        )}
        <div className="min-w-0 text-sm">
          <p className="font-medium">
            {busy ? 'Reading the order…' : 'Ordered online? Import the order PDF'}
          </p>
          <p className="text-xs text-muted-foreground">
            {busy
              ? 'Pulling the vendor, order #, items, prices and totals out of the document.'
              : 'Drop the order confirmation, receipt or invoice here. Atlas pre-fills this PO and attaches the PDF as the order receipt — you review before creating it.'}
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        <FileUp className="mr-1 h-3.5 w-3.5" />
        Choose PDF
      </Button>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={PO_IMPORT_ACCEPT}
        aria-label="Import order PDF"
        onChange={(e) => {
          take(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

interface ImportBannerProps {
  fileName: string;
  summary: string | null;
  prefilledCount: number;
  issues: ReviewIssue[];
  onOpenFile: () => void;
  onReplace: (file: File) => void;
  onReject: (message: string) => void;
  onFocusIssue: (issue: ReviewIssue) => void;
}

/** Sits at the top of the form after an import: what was filled, what is left. */
export function PoImportBanner({
  fileName,
  summary,
  prefilledCount,
  issues,
  onOpenFile,
  onReplace,
  onReject,
  onFocusIssue,
}: ImportBannerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const required = issues.filter((i) => i.tone === 'required');
  const checks = issues.filter((i) => i.tone === 'check');
  return (
    <div className="space-y-3 rounded-md border border-atmosphere-dark/40 bg-atmosphere/10 p-4 dark:border-atmosphere/40">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <Sparkles className="h-4 w-4 text-info" />
            Pre-filled from{' '}
            <button
              type="button"
              onClick={onOpenFile}
              className="inline-flex min-w-0 items-center gap-1 truncate underline-offset-2 hover:underline"
              title="Open the PDF to compare"
            >
              <span className="truncate">{fileName}</span>
              <ExternalLink className="h-3 w-3 shrink-0" />
            </button>
          </p>
          {summary && <p className="text-xs text-muted-foreground">{summary}</p>}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => inputRef.current?.click()}
        >
          Use a different PDF
        </Button>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={PO_IMPORT_ACCEPT}
          aria-label="Replace imported order PDF"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            if (isImportableOrderFile(file)) onReplace(file);
            else onReject('Pick the order as a PDF (or a PNG / JPG screenshot).');
          }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm border border-atmosphere-dark/50 bg-atmosphere/30" />
          {prefilledCount} field{prefilledCount === 1 ? '' : 's'} pre-filled from the PDF
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm border border-fresh-salmon-dark bg-fresh-salmon/20" />
          {required.length === 0
            ? 'All required fields are filled'
            : `${required.length} required field${required.length === 1 ? '' : 's'} need${
                required.length === 1 ? 's' : ''
              } your input`}
        </span>
        {checks.length > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm border border-fresh-salmon bg-atmosphere/15" />
            {checks.length} to double-check
          </span>
        )}
      </div>

      {issues.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {[...required, ...checks].map((issue) => (
            <li key={`${issue.tone}:${issue.targetId}:${issue.label}`}>
              <button
                type="button"
                onClick={() => onFocusIssue(issue)}
                title={issue.hint}
                className={cn(
                  'inline-flex max-w-[20rem] items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                  issue.tone === 'required'
                    ? 'border-fresh-salmon-dark bg-fresh-salmon/15 text-fresh-salmon-dark hover:bg-fresh-salmon/25'
                    : 'border-fresh-salmon/60 bg-card text-foreground hover:bg-secondary/60',
                )}
              >
                <AlertCircle className="h-3 w-3 shrink-0" />
                <span className="truncate">{issue.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
